import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";

type ToastType = "success" | "error" | "warning" | "info";

export interface ToastOptions {
  message: string;
  title?: string;
  type?: ToastType;
  duration?: number;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "primary" | "danger" | "warning";
}

interface ToastItem extends Required<ToastOptions> {
  id: number;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastBridge: ToastContextValue | null = null;

const toastMeta: Record<ToastType, { icon: React.ElementType; title: string; classes: string }> = {
  success: {
    icon: FaCheckCircle,
    title: "Success",
    classes: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
  error: {
    icon: FaExclamationCircle,
    title: "Error",
    classes: "border-rose-200 bg-rose-50 text-rose-800",
  },
  warning: {
    icon: FaExclamationTriangle,
    title: "Warning",
    classes: "border-amber-200 bg-amber-50 text-amber-800",
  },
  info: {
    icon: FaInfoCircle,
    title: "Info",
    classes: "border-blue-200 bg-blue-50 text-blue-800",
  },
};

const inferToastType = (message: string): ToastType => {
  if (/success|created|updated|saved|approved|rejected|converted|deleted|generated|recorded/i.test(message)) {
    return "success";
  }

  if (/required|invalid|select|fill/i.test(message)) {
    return "warning";
  }

  return "error";
};

export const notify = {
  show: (options: ToastOptions) => {
    if (toastBridge) {
      toastBridge.showToast(options);
      return;
    }

    if (options.type === "error") {
      console.error(options.message);
    }
  },
  success: (message: string, title?: string) => toastBridge?.success(message, title),
  error: (message: string, title?: string) => {
    if (toastBridge) {
      toastBridge.error(message, title);
      return;
    }

    console.error(message);
  },
  warning: (message: string, title?: string) => toastBridge?.warning(message, title),
  info: (message: string, title?: string) => toastBridge?.info(message, title),
};

export const confirmAction = async (options: ConfirmOptions): Promise<boolean> => {
  const message = options.title ? `${options.title}\n\n${options.message}` : options.message;
  return window.confirm(message);
};

export const showMessage = (message: string, type?: ToastType) => {
  notify.show({
    message,
    type: type || inferToastType(message),
  });
};

export const showConfirmation = (message: string, options: Omit<ConfirmOptions, "message"> = {}) =>
  confirmAction({
    title: options.title || "Confirm action",
    message,
    confirmLabel: options.confirmLabel || "Confirm",
    cancelLabel: options.cancelLabel || "Cancel",
    tone: options.tone || "warning",
  });

export const showDeleteConfirmation = (message: string) =>
  confirmAction({
    title: "Delete item",
    message,
    confirmLabel: "Delete",
    cancelLabel: "Cancel",
    tone: "danger",
  });

const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const removeToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ message, title, type = "info", duration = 4500 }: ToastOptions) => {
      const id = nextId.current;
      nextId.current += 1;

      setToasts((current) => [
        ...current,
        {
          id,
          message,
          title: title || toastMeta[type].title,
          type,
          duration,
        },
      ]);

      if (duration > 0) {
        window.setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  const value: ToastContextValue = {
    showToast,
    success: (message, title) => showToast({ message, title, type: "success" }),
    error: (message, title) => showToast({ message, title, type: "error" }),
    warning: (message, title) => showToast({ message, title, type: "warning" }),
    info: (message, title) => showToast({ message, title, type: "info" }),
  };

  useEffect(() => {
    toastBridge = value;
    return () => {
      if (toastBridge === value) {
        toastBridge = null;
      }
    };
  }, [value]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-3 top-3 z-[70] flex w-[calc(100vw-1.5rem)] max-w-sm flex-col gap-2 sm:right-4 sm:top-4">
        {toasts.map((toast) => {
          const meta = toastMeta[toast.type];
          const Icon = meta.icon;

          return (
            <div
              key={toast.id}
              className={`cms-type-state-text flex items-start gap-3 rounded-lg border px-3 py-3 shadow-lg ${meta.classes}`}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <Icon className="mt-0.5 shrink-0 text-base" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{toast.title}</div>
                <div className="cms-type-helper mt-0.5">{toast.message}</div>
              </div>
              <button
                type="button"
                className="shrink-0 rounded p-1 opacity-70 transition hover:bg-white/60 hover:opacity-100"
                onClick={() => removeToast(toast.id)}
                aria-label="Dismiss notification"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const FeedbackProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ToastProvider>{children}</ToastProvider>
);

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used inside FeedbackProvider");
  }

  return context;
};
